// services/UserService.js
import { db } from "./firebase";
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp,
} from "firebase/firestore";

/**
 * Service for managing user data in Firebase
 */
class UserService {
    /**
     * Get user data by ID
     * @param {string} userId - The user ID
     * @returns {Promise<Object|null>} - User data or null if not found
     */
    async getUserById(userId) {
        try {
            if (!userId) {
                console.error("Missing user ID for getUserById");
                return null;
            }

            const userRef = doc(db, "users", userId);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                console.warn(`No user found with ID ${userId}`);
                return null;
            }

            const data = userDoc.data();

            return {
                id: userDoc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || null,
                updatedAt: data.updatedAt?.toDate() || null,
                lastLogin: data.lastLogin?.toDate() || null,
                lastLogout: data.lastLogout?.toDate() || null,
            };
        } catch (error) {
            console.error("Error fetching user:", error);
            return null;
        }
    }

    /**
     * Update user's personal information
     * @param {string} userId - The user ID
     * @param {Object} personalInfo - Personal information data
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async updatePersonalInformation(userId, personalInfo) {
        try {
            if (!userId) {
                console.error("Missing user ID for updatePersonalInformation");
                return false;
            }

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                personal_information: {
                    currentJobTitle: personalInfo.currentJobTitle || "",
                    yearsExperience: personalInfo.yearsExperience || null,
                    currentCompany: personalInfo.currentCompany || "",
                    previousCompanies: personalInfo.previousCompanies || "",
                },
                updatedAt: serverTimestamp(),
            });

            console.log(`Personal information updated for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error updating personal information:", error);
            return false;
        }
    }

    /**
     * Update user's education information
     * @param {string} userId - The user ID
     * @param {Array} educationEntries - Array of education entries
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async updateEducationInformation(userId, educationEntries) {
        try {
            if (!userId) {
                console.error("Missing user ID for updateEducationInformation");
                return false;
            }

            // Clean up education entries - remove UI-specific fields
            const cleanEducationEntries = educationEntries.map(entry => ({
                id: entry.id,
                degree: entry.degree || "",
                field: entry.field || "",
                graduationYear: entry.graduationYear || "",
                institute: entry.institute || "",
            }));

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                education: cleanEducationEntries,
                updatedAt: serverTimestamp(),
            });

            console.log(`Education information updated for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error updating education information:", error);
            return false;
        }
    }

    /**
     * Update user's job preferences
     * @param {string} userId - The user ID
     * @param {Object} jobPreferences - Job preferences data
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async updateJobPreferences(userId, jobPreferences) {
        try {
            if (!userId) {
                console.error("Missing user ID for updateJobPreferences");
                return false;
            }

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                job_preferences: {
                    currentSalary: jobPreferences.currentSalary || null,
                    noticePeriod: jobPreferences.noticePeriod || "",
                },
                updatedAt: serverTimestamp(),
            });

            console.log(`Job preferences updated for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error updating job preferences:", error);
            return false;
        }
    }

    async updateWorkHistory(userId, workHistoryEntries) {
        try {
            if (!userId) {
                console.error("Missing user ID for updateWorkHistory");
                return false;
            }

            // Clean up work history entries - remove UI-specific fields
            const cleanWorkHistoryEntries = workHistoryEntries.map(entry => ({
                id: entry.id,
                companyName: entry.companyName || "",
                role: entry.role || "",
                yearsWorked: entry.yearsWorked || "",
            }));

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                work_history: cleanWorkHistoryEntries,
                updatedAt: serverTimestamp(),
            });

            console.log(`Work history updated for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error updating work history:", error);
            return false;
        }
    }

    /**
     * Upload and update user's resume
     * @param {string} userId - The user ID
     * @param {File} resumeFile - The resume file
     * @returns {Promise<Object|null>} - Resume info or null if failed
     */
    async uploadResume(userId, resumeFile) {
        try {
            if (!userId || !resumeFile) {
                console.error("Missing required parameters for uploadResume");
                return null;
            }

            // Validate file type (allow PDF, DOC, DOCX)
            const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ];

            if (!allowedTypes.includes(resumeFile.type)) {
                throw new Error("Only PDF, DOC, and DOCX files are allowed");
            }

            // Validate file size (10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (resumeFile.size > maxSize) {
                throw new Error("File size must be less than 10MB");
            }

            // Create FormData object for file upload
            const formData = new FormData();
            formData.append('userId', userId);
            formData.append('resumeFile', resumeFile);

            // Make API call to upload resume
            const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/upload-resume`, {
                method: 'POST',
                body: formData, // Don't set Content-Type header when using FormData
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }

            console.log(`Resume uploaded successfully for user: ${userId}`);
            return result.data;

        } catch (error) {
            console.error("Error uploading resume:", error);
            throw error;
        }
    }


    /**
     * Remove user's resume
     * @param {string} userId - The user ID
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async removeResume(userId) {
        try {
            if (!userId) {
                console.error("Missing user ID for removeResume");
                return false;
            }

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                resume: null,
                updatedAt: serverTimestamp(),
            });

            console.log(`Resume removed for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error removing resume:", error);
            return false;
        }
    }

    /**
     * Update user's complete profile data
     * @param {string} userId - The user ID
     * @param {Object} profileData - Complete profile data
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async updateCompleteProfile(userId, profileData) {
        try {
            if (!userId) {
                console.error("Missing user ID for updateCompleteProfile");
                return false;
            }

            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                ...profileData,
                updatedAt: serverTimestamp(),
            });

            console.log(`Complete profile updated for user: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error updating complete profile:", error);
            return false;
        }
    }

    /**
     * Initialize user data when they first sign up
     * @param {string} userId - The user ID
     * @param {Object} initialData - Initial user data
     * @returns {Promise<boolean>} - Whether the operation succeeded
     */
    async initializeUser(userId, initialData) {
        try {
            if (!userId) {
                console.error("Missing user ID for initializeUser");
                return false;
            }

            const userRef = doc(db, "users", userId);

            await setDoc(userRef, {
                ...initialData,
                personal_information: {},
                education: [],
                job_preferences: {},
                resume: null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            }, { merge: true });

            console.log(`User initialized: ${userId}`);
            return true;
        } catch (error) {
            console.error("Error initializing user:", error);
            return false;
        }
    }
}

export default new UserService();